import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategoryExplorer() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <section id="categories" className="w-full px-4 md:px-6 py-14 md:py-20">
      <div className="mx-auto max-w-7xl">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950">{t('category.title')}</h2>
          <p className="mt-3 text-foreground-600 text-sm md:text-base">{t('category.subtitle')}</p>
        </div>

        {/* Dropdown */}
        <div className="mt-8 mx-auto max-w-md relative" ref={ref}>
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-between h-12 px-5 rounded-lg bg-background-100 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors"
          >
            <span className="flex items-center gap-2">
              <i className="ri-apps-2-line text-lg text-primary-600" />
              {t('category.dropdownLabel')}
            </span>
            <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div className="absolute z-20 mt-2 w-full max-h-80 overflow-y-auto bg-background-50 border border-background-200 rounded-lg p-1">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                    <i className={`${cat.icon} text-base`} />
                  </span>
                  {t(cat.key)}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Category grid */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group block rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
            >
              <div className="relative w-full h-40 md:h-44 overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${t(cat.key)} — USFans Spreadsheet category`}
                  title={`${t(cat.key)} — USFans Spreadsheet category`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="px-3 py-3 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-foreground-900 truncate whitespace-nowrap">{t(cat.key)}</span>
                <span className="w-6 h-6 flex items-center justify-center shrink-0 text-primary-600 group-hover:translate-x-0.5 transition-transform">
                  <i className="ri-arrow-right-up-line text-base" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}